import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { Add, Delete, ViewList, ViewModule, Visibility } from "@material-ui/icons";
import { css } from "@emotion/css";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CreatorLabel from "./CreatorLabel";
import { CTable } from "shared";
import CCell from "shared/CTable/CCell";
import { isUsMarket } from "utils/market";
import { CatalogueKind, CatalogueRollout } from "routes/createContent/api/catalogueDeployments";
import { CatalogueDeploymentRow } from "./CatalogueDeploymentRow";
import { LibraryItem } from "./LibraryItem/LibraryItem";
import { CardContainer } from "./styles";
import { isContentOnly } from "utils/appMode";
import { LOCALES, TARGET_JOINTS } from "../views/lessons/constants";

const DEFAULT_LOCALE = isUsMarket ? "en-us" : "sv-se";
const menuProps = { disableScrollLock: true };
const ROWS_PER_PAGE = [10, 25, 50, 100, 200, 500];

export const useCatalogueListControls = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [joints, setJoints] = useState<string[]>([]);
  const [locale, setLocale] = useState(DEFAULT_LOCALE);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [view, setView] = useState<"table" | "cards">("table");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const filterKey = JSON.stringify([debouncedSearch, joints, locale, limit]);
  useEffect(() => setPage(0), [filterKey]);

  return {
    search,
    setSearch,
    debouncedSearch,
    joints,
    setJoints,
    locale,
    setLocale,
    page,
    setPage,
    limit,
    setLimit,
    view,
    setView,
  };
};

export type CatalogueListControls = ReturnType<typeof useCatalogueListControls>;

const CatalogueListHeader: React.VFC<{ title: string; newLabel: string; newPath: string }> = ({
  title,
  newLabel,
  newPath,
}) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
    <h2 style={{ margin: 0 }}>{title}</h2>
    <Button color="primary" variant="contained" size="small" startIcon={<Add />} component={Link} to={newPath}>
      {newLabel}
    </Button>
  </Box>
);

const CatalogueLocaleSelect: React.VFC<{ value: string; onChange: (locale: string) => void }> = ({
  value,
  onChange,
}) => (
  <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
    <InputLabel>Locale</InputLabel>
    <Select label="Locale" value={value} onChange={e => onChange(e.target.value as string)} MenuProps={menuProps}>
      {LOCALES.map(l => (
        <MenuItem key={l.code} value={l.code}>
          {l.flag} {l.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const MultiSelectFilter: React.VFC<{
  label: string;
  options: readonly string[];
  value: string[];
  onChange: (value: string[]) => void;
}> = ({ label, options, value, onChange }) => (
  <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
    <InputLabel>{label}</InputLabel>
    <Select
      multiple
      label={label}
      value={value}
      onChange={e => onChange(e.target.value as string[])}
      renderValue={selected => (selected as string[]).join(", ")}
      MenuProps={menuProps}
    >
      {options.map(option => (
        <MenuItem key={option} value={option}>
          <Checkbox size="small" checked={value.includes(option)} />
          {humanize(option)}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const ViewToggle: React.VFC<{ controls: CatalogueListControls }> = ({ controls }) => (
  <Box display="flex">
    <Tooltip title="Table">
      <IconButton
        size="small"
        color={controls.view === "table" ? "primary" : "default"}
        onClick={() => controls.setView("table")}
      >
        <ViewList />
      </IconButton>
    </Tooltip>
    <Tooltip title="Cards">
      <IconButton
        size="small"
        color={controls.view === "cards" ? "primary" : "default"}
        onClick={() => controls.setView("cards")}
      >
        <ViewModule />
      </IconButton>
    </Tooltip>
  </Box>
);

const CataloguePagination: React.VFC<{ controls: CatalogueListControls; count: number }> = ({ controls, count }) => (
  <TablePagination
    rowsPerPageOptions={ROWS_PER_PAGE}
    component="div"
    count={count}
    rowsPerPage={controls.limit}
    page={controls.page}
    onPageChange={(_e, page) => controls.setPage(page)}
    onRowsPerPageChange={e => controls.setLimit(parseInt(e.target.value, 10))}
    SelectProps={{ MenuProps: menuProps }}
  />
);

const humanize = (value: string) => value.replaceAll("_", " ");

export const catalogueQuery = (controls: CatalogueListControls) => ({
  page: controls.page,
  limit: controls.limit,
  search: controls.debouncedSearch,
  joints: controls.joints,
  locale: controls.locale,
});

export type CatalogueQuery = ReturnType<typeof catalogueQuery>;

export type CatalogueItem = {
  id: number;
  name: string;
  title: string;
  image: string;
  target_joints?: string[] | null;
  updated_at?: string | null;
  creator_id?: number | null;
  rollout?: CatalogueRollout | null;
};

export type CatalogueExtraColumn<Raw> = {
  key: string;
  label: string;
  width: number;
  value: (raw: Raw) => string | null | undefined;
};

export type CatalogueExtraFilter = {
  label: string;
  options: readonly string[];
  value: string[];
  onChange: (value: string[]) => void;
};

type Row<Raw> = CatalogueItem & { raw: Raw } & Record<string, unknown>;

type CatalogueListProps<Raw> = {
  kind: CatalogueKind;
  title: string;
  noun: string;
  newPath: string;
  editPath: (id: number) => string;
  controls: CatalogueListControls;
  query: {
    data?: { items: Raw[]; metadata?: { total_count?: number } };
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  toItem: (raw: Raw) => CatalogueItem;
  deletion: {
    mutate: (id: number, options: { onSuccess: () => void }) => void;
    reset: () => void;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  extraColumns?: CatalogueExtraColumn<Raw>[];
  extraFilters?: CatalogueExtraFilter[];
  pickerMode?: boolean;
  checkedIds?: number[];
  onPickToggle?: (raw: Raw) => void;
  onPreview?: (id: number) => void;
};

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));

export const CatalogueList = <Raw,>({
  kind,
  title,
  noun,
  newPath,
  editPath,
  controls,
  query,
  toItem,
  deletion,
  extraColumns = [],
  extraFilters = [],
  pickerMode = false,
  checkedIds = [],
  onPickToggle,
  onPreview,
}: CatalogueListProps<Raw>) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const { setPage } = controls;
  const extraFilterKey = JSON.stringify(extraFilters.map(filter => filter.value));
  useEffect(() => setPage(0), [extraFilterKey, setPage]);

  const rows: Row<Raw>[] = (query.data?.items ?? []).map(raw => {
    const item = toItem(raw);
    return {
      ...Object.fromEntries(extraColumns.map(column => [column.key, column.value(raw)])),
      ...item,
      title: item.title || "Untitled",
      raw,
    };
  });
  const count = query.data?.metadata?.total_count ?? rows.length;
  const open = pickerMode ? undefined : (row: Row<Raw>) => history.push(editPath(row.id));
  const askDelete = (id: number) => {
    deletion.reset();
    setConfirmDelete(id);
  };
  const filtered = controls.joints.length > 0 || extraFilters.some(filter => filter.value.length > 0);
  const pagination = <CataloguePagination controls={controls} count={count} />;

  const header: Record<string, string> = pickerMode
    ? { selected: "", image: "Image", title: "Title", target_joints: "Joints", actions: "" }
    : {
        id: "ID",
        image: "Image",
        title: "Title",
        target_joints: "Joints",
        ...Object.fromEntries(extraColumns.map(column => [column.key, column.label])),
        updated_at: "Updated",
        ...(isContentOnly ? { deployment: "Deployment" } : { creator_id: t("creator.title") }),
        actions: "",
      };

  const widths: Record<string, number | undefined> = {
    id: 64,
    selected: 64,
    image: 72,
    title: 320,
    ...Object.fromEntries(extraColumns.map(column => [column.key, column.width])),
    updated_at: 80,
    deployment: 190,
    creator_id: 160,
    actions: pickerMode ? 120 : 56,
  };

  const cells = (row: Row<Raw>): Record<string, React.ReactElement> => ({
    id: <CCell>{row.id}</CCell>,
    selected: (
      <CCell>
        <Checkbox checked={checkedIds.includes(row.id)} onChange={() => onPickToggle?.(row.raw)} />
      </CCell>
    ),
    image: <ImageCell src={row.image} />,
    title: (
      <CCell>
        <div className={styles.ellipsis} title={row.title}>
          {row.title}
        </div>
      </CCell>
    ),
    target_joints: (
      <CCell>
        <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
          {(row.target_joints ?? []).map(j => (
            <Chip key={j} label={j} size="small" variant="outlined" />
          ))}
        </Box>
      </CCell>
    ),
    ...Object.fromEntries(
      extraColumns.map(column => {
        const value = row[column.key];
        return [column.key, <CCell>{typeof value === "string" ? humanize(value) : "–"}</CCell>];
      })
    ),
    updated_at: (
      <CCell>
        {row.updated_at
          ? new Date(row.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : "–"}
      </CCell>
    ),
    creator_id: (
      <CCell>
        <CreatorLabel creatorId={row.creator_id} />
      </CCell>
    ),
    deployment: (
      <CCell>
        <CatalogueDeploymentRow kind={kind} name={row.name} rollout={row.rollout ?? undefined} compact />
      </CCell>
    ),
    actions: pickerMode ? (
      <CCell>
        <Button variant="outlined" size="small" startIcon={<Visibility />} onClick={() => onPreview?.(row.id)}>
          Preview
        </Button>
      </CCell>
    ) : (
      <CCell>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            aria-label="Delete"
            onClick={e => {
              e.stopPropagation();
              askDelete(row.id);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </CCell>
    ),
  });

  const results = () => {
    if (query.isError) {
      return (
        <Box color="error.main" my={2}>
          Failed to load: {errorMessage(query.error)}
        </Box>
      );
    }
    if (query.isLoading && rows.length === 0) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }
    if (rows.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          No {noun}s found
        </Box>
      );
    }
    if (controls.view === "cards" && !pickerMode) {
      return (
        <CardContainer>
          {rows.map(row => (
            <LibraryItem
              key={row.id}
              id={row.id}
              title={row.title}
              image={<img alt="" src={row.image} height="100%" />}
              tags={row.target_joints ?? []}
              onClick={() => open?.(row)}
              onDelete={() => askDelete(row.id)}
              deployment={
                isContentOnly ? (
                  <CatalogueDeploymentRow kind={kind} name={row.name} rollout={row.rollout ?? undefined} />
                ) : undefined
              }
            />
          ))}
        </CardContainer>
      );
    }
    return (
      <CTable<Row<Raw>, string>
        size="small"
        className={styles.table}
        items={rows}
        header={header}
        cellFactory={cells}
        columnWidths={widths}
        defaultSortKey="title"
        onRowClick={open}
      />
    );
  };

  return (
    <div>
      {!pickerMode && <CatalogueListHeader title={title} newLabel={`New ${noun}`} newPath={newPath} />}

      <Box mb={2} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={controls.search}
          onChange={e => controls.setSearch(e.target.value)}
          placeholder="Search by title"
          style={{ flex: 1, minWidth: 200 }}
        />
        <MultiSelectFilter
          label="Joint"
          options={TARGET_JOINTS}
          value={controls.joints}
          onChange={controls.setJoints}
        />
        {extraFilters.map(filter => (
          <MultiSelectFilter key={filter.label} {...filter} />
        ))}
        {filtered && (
          <Button
            size="small"
            onClick={() => {
              controls.setJoints([]);
              extraFilters.forEach(filter => filter.onChange([]));
            }}
          >
            Clear
          </Button>
        )}
        <CatalogueLocaleSelect value={controls.locale} onChange={controls.setLocale} />
        {!pickerMode && <ViewToggle controls={controls} />}
      </Box>

      {deletion.isError && (
        <Box color="error.main" my={2}>
          {errorMessage(deletion.error)}
        </Box>
      )}

      {rows.length > 0 && pagination}
      {results()}
      {rows.length > 0 && pagination}

      <ConfirmDeleteDialog
        noun={noun}
        open={confirmDelete != null}
        loading={deletion.isLoading}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete == null) return;
          deletion.mutate(confirmDelete, { onSuccess: () => setConfirmDelete(null) });
        }}
      />
    </div>
  );
};

const ImageCell: React.VFC<{ src?: string | null }> = ({ src }) => (
  <CCell>
    {src ? (
      <Tooltip title={<img src={src} alt="" className={styles.preview} />} placement="right">
        <img src={src} alt="" className={styles.thumb} />
      </Tooltip>
    ) : (
      <Box className={styles.noImage}>--</Box>
    )}
  </CCell>
);

export const ConfirmDeleteDialog: React.VFC<{
  noun: string;
  open: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ noun, open, loading, onCancel, onConfirm }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>Delete this {noun}?</DialogTitle>
    <DialogContent>
      <DialogContentText>
        This permanently deletes the {noun} with all of its translations. This action cannot be undone.
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      <Button
        color="secondary"
        variant="contained"
        onClick={onConfirm}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} /> : undefined}
      >
        Delete {noun}
      </Button>
    </DialogActions>
  </Dialog>
);

const styles = {
  table: css`
    table-layout: fixed;
    width: 100%;
  `,
  thumb: css`
    width: 48px;
    height: 36px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
  `,
  preview: css`
    width: 320px;
    height: 240px;
    object-fit: cover;
    display: block;
  `,
  noImage: css`
    width: 48px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #bbb;
    font-size: 12px;
  `,
  ellipsis: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
};
